/** @file cornerMesh: one dynamic Babylon mesh holding a quad per impostor, rewritten every frame. */
import type { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial.js';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer.js';
import { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { ImpostorList } from '@zilo/pose3d-render-core/contract';
import type { QuadFrame } from '@zilo/pose3d-runtime-babylon/draw/quadFrame';
import { quadVertices } from '@zilo/pose3d-runtime-babylon/draw/quadVertices';

/** The custom vertex streams (name, floats per vertex) next to the positions; the shaders read them by these names. */
const STREAMS = [['geomA', 4], ['geomB', 4], ['depth3', 3], ['cut3', 3], ['texRect', 4]] as const;

/** Float32 streams for a number of quads. */
type Streams = { positions: Float32Array; geomA: Float32Array; geomB: Float32Array; depth3: Float32Array; cut3: Float32Array; texRect: Float32Array };

/** Zeroed streams for `quads` quads. */
const streams = (quads: number): Streams => ({
  positions: new Float32Array(quads * 12), geomA: new Float32Array(quads * 16), geomB: new Float32Array(quads * 16),
  depth3: new Float32Array(quads * 12), cut3: new Float32Array(quads * 12), texRect: new Float32Array(quads * 16),
});

/** A mesh of `capacity()` quads; `write` uploads the entries of `order` and zeroes every slot after them. */
export type CornerMesh = {
  mesh: Mesh;
  capacity(): number;
  write(list: ImpostorList, order: readonly number[], frame: QuadFrame, sheetSize: [number, number]): number;
  dispose(): void;
};

/** Two triangles per quad over its corners `[tl, tr, br, bl]`: `4i, 4i+1, 4i+2` and `4i, 4i+2, 4i+3`. */
function quadIndices(quads: number): Uint32Array {
  const out = new Uint32Array(quads * 6);
  for (let i = 0; i < quads; i++) out.set([4 * i, 4 * i + 1, 4 * i + 2, 4 * i, 4 * i + 2, 4 * i + 3], i * 6);
  return out;
}

/**
 * An updatable mesh (`alwaysSelectAsActiveMesh`, not pickable) with `initialCapacity` quads. `write` fills slot `i`
 * with `quadVertices(list, order[i])`, zeroes the slots from `order.length` to the capacity (degenerate, so a quad
 * that disappeared is not drawn stale), grows the capacity by doubling (re-applying the vertex data) and returns
 * `order.length`.
 */
export function cornerMesh(scene: Scene, material: ShaderMaterial, name: string, initialCapacity: number): CornerMesh {
  const mesh = new Mesh(name, scene);
  mesh.material = material;
  mesh.alwaysSelectAsActiveMesh = true;
  mesh.isPickable = false;
  let cap = 0;
  let buf: Streams = streams(0);
  const allocate = (quads: number): void => {
    cap = quads;
    buf = streams(quads);
    const data = new VertexData();
    data.positions = buf.positions;
    data.indices = quadIndices(quads);
    data.applyToMesh(mesh, true);
    for (const [kind, size] of STREAMS) mesh.setVerticesData(kind, buf[kind], true, size);
  };
  allocate(Math.max(1, initialCapacity));
  return {
    mesh,
    capacity: () => cap,
    write: (list, order, frame, sheetSize) => {
      let next = cap;
      while (next < order.length) next *= 2;
      if (next !== cap) allocate(next);
      order.forEach((index, slot) => { quadVertices(list, index, frame, sheetSize, buf, slot); });
      buf.positions.fill(0, order.length * 12);
      mesh.updateVerticesData(VertexBuffer.PositionKind, buf.positions);
      for (const [kind, size] of STREAMS) {
        buf[kind].fill(0, order.length * 4 * size);
        mesh.updateVerticesData(kind, buf[kind]);
      }
      return order.length;
    },
    dispose: () => { mesh.dispose(); },
  };
}
